/**
 * Print Utilities
 *
 * printHtmlViaIframe handles HTML content (QR codes, garment labels) via a
 * hidden iframe so the browser print dialog opens without a visible popup flash.
 *
 * HTML only. Do not pass PDF blob URLs to this helper: Chrome's PDF plugin
 * renders in a separate process where contentWindow.print() has no effect,
 * so the iframe-driven approach silently does nothing for PDFs. Open PDFs in
 * a new tab (window.open) and let the built-in viewer's Print button handle it.
 */

function createHiddenIframe(): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  return iframe;
}

function removeIframe(iframe: HTMLIFrameElement): void {
  if (iframe.parentNode) {
    document.body.removeChild(iframe);
  }
}

/** Longest printing waits on images before it prints whatever has loaded. */
export const IMAGE_WAIT_CAP_MS = 5000;

/**
 * Resolve once every `<img>` in the document that is not yet `complete` has
 * fired `load` or `error`, or after `capMs`, whichever comes first. Resolves
 * immediately when nothing is pending, so text-only prints stay instant.
 */
function waitForImages(doc: Document, capMs: number = IMAGE_WAIT_CAP_MS): Promise<void> {
  const pending = Array.from(doc.images).filter((img) => !img.complete);
  if (pending.length === 0) return Promise.resolve();

  return new Promise((resolve) => {
    let remaining = pending.length;
    // resolve() and clearTimeout() are both idempotent, so no guard flag.
    const finish = () => {
      clearTimeout(capTimer);
      resolve();
    };
    const capTimer = setTimeout(finish, capMs);

    // An image fires exactly one of load/error per src, so one decrement each.
    const settle = () => {
      remaining -= 1;
      if (remaining === 0) finish();
    };
    for (const img of pending) {
      img.addEventListener('load', settle, { once: true });
      img.addEventListener('error', settle, { once: true });
    }
  });
}

/**
 * Hide images that finished loading with no pixels (dead host, 404) so the
 * browser's broken-image icon never reaches paper. `visibility` rather than
 * `display` keeps the reserved box, so the surrounding layout does not shift.
 */
function hideBrokenImages(doc: Document): void {
  for (const img of Array.from(doc.images)) {
    if (img.complete && img.naturalWidth === 0) {
      img.style.visibility = 'hidden';
    }
  }
}

/**
 * Print HTML content via a hidden iframe.
 *
 * Creates a temporary invisible iframe, writes the HTML into it, waits for
 * its images to load (capped at IMAGE_WAIT_CAP_MS), hides any that failed,
 * triggers the browser print dialog, and cleans up after printing.
 *
 * Images must not use `loading="lazy"`: the iframe is 0x0, so a lazy image
 * never enters the viewport, never loads, and holds printing to the cap.
 *
 * @param html - Complete HTML document string to print
 * @param onAfterPrint - Called when the browser print dialog closes (print or cancel)
 */
export function printHtmlViaIframe(html: string, onAfterPrint?: () => void): void {
  const iframe = createHiddenIframe();

  // Append first — about:blank loads synchronously
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDoc) {
    removeIframe(iframe);
    throw new Error('Failed to access iframe document for printing');
  }

  // Write content
  iframeDoc.open();
  iframeDoc.write(html); // document.write required for full HTML documents in iframe printing
  iframeDoc.close();

  // Extract <title> from the HTML to use as document.title during printing.
  // Chrome uses the parent page title (not the iframe's) for "Save as PDF" filename.
  const titleMatch = html.match(/<title>(.*?)<\/title>/i);
  const iframeTitle = titleMatch?.[1] ?? '';

  // Print once content and resources (images, etc.) finish loading.
  // Guard prevents double-trigger from both the load listener and the
  // timeout fallback (for when load fires synchronously during close()).
  let printed = false;
  let afterPrintFired = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;

    // Temporarily override parent document title so Chrome's print dialog
    // suggests the correct filename instead of the SPA page title.
    const originalTitle = document.title;
    if (iframeTitle) document.title = iframeTitle;

    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();

    // Restore after a tick — the print dialog captures the title synchronously.
    setTimeout(() => { document.title = originalTitle; }, 200);

    iframe.contentWindow?.addEventListener('afterprint', () => {
      if (afterPrintFired) return;
      afterPrintFired = true;
      document.title = originalTitle;
      removeIframe(iframe);
      onAfterPrint?.();
    });
  };

  // Both entry points below funnel through startPrint. Whichever fires first
  // waits for images (the 100ms fallback would otherwise beat them and print
  // blank photo boxes), hides broken ones, then prints once.
  let started = false;
  const startPrint = () => {
    if (started) return;
    started = true;
    void waitForImages(iframeDoc).then(() => {
      hideBrokenImages(iframeDoc);
      triggerPrint();
    });
  };

  // Primary: wait for images/resources to load
  iframe.contentWindow?.addEventListener('load', startPrint);

  // Fallback: if load fired synchronously during close() (pure HTML, no images),
  // the listener above may have missed it. Short timeout ensures print still happens.
  setTimeout(startPrint, 100);

  // Final fallback cleanup after 15 seconds in case afterprint never fires
  setTimeout(() => {
    if (!afterPrintFired) {
      afterPrintFired = true;
      removeIframe(iframe);
      onAfterPrint?.();
    }
  }, 15000);
}

/**
 * Trigger a file download for a blob URL.
 */
export function downloadBlob(blobUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
